// import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import axios from "../../axiosInstance";


import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../axiosInstance";

// Async Thunks
export const createTestimonial = createAsyncThunk(
  'testimonials/createTestimonial',
  async (testimonialData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/testimonial/', testimonialData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create testimonial');
    }
  }
);

export const getAllTestimonials = createAsyncThunk(
  'testimonials/getAllTestimonials',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/testimonial/all');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch testimonials');
    }
  }
);

export const getUserTestimonials = createAsyncThunk(
  'testimonials/getUserTestimonials',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/testimonial/my');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user testimonials');
    }
  }
);

export const updateTestimonial = createAsyncThunk(
  'testimonials/updateTestimonial',
  async ({ id, testimonialData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`/api/v1/testimonial/${id}`, testimonialData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update testimonial');
    }
  }
);

export const deleteTestimonial = createAsyncThunk(
  'testimonials/deleteTestimonial',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/v1/testimonial/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete testimonial');
    }
  }
);

const testimonialSlice = createSlice({
  name: 'testimonials',
  initialState: {
    testimonials: [],
    userTestimonials: [],
    loading: false,
    error: null,
    success: false,
    message: '',
  },
  reducers: {
    clearTestimonialState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = '';
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Testimonial
      .addCase(createTestimonial.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createTestimonial.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        state.userTestimonials.unshift(action.payload.newTestimonial);
      })
      .addCase(createTestimonial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Get All Testimonials
      .addCase(getAllTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTestimonials.fulfilled, (state, action) => {
        state.loading = false;
        state.testimonials = action.payload.testimonials;
      })
      .addCase(getAllTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get User Testimonials
      .addCase(getUserTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserTestimonials.fulfilled, (state, action) => {
        state.loading = false;
        state.userTestimonials = action.payload.testimonials;
      })
      .addCase(getUserTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Testimonial
      .addCase(updateTestimonial.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateTestimonial.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        
        // Update in userTestimonials array
        const index = state.userTestimonials.findIndex(
          (item) => item._id === action.payload.testimonial._id
        );
        if (index !== -1) {
          state.userTestimonials[index] = action.payload.testimonial;
        }
        
        // Update in all testimonials array if needed
        const allIndex = state.testimonials.findIndex(
          (item) => item._id === action.payload.testimonial._id
        );
        if (allIndex !== -1) {
          state.testimonials[allIndex] = action.payload.testimonial;
        }
      })
      .addCase(updateTestimonial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete Testimonial
      .addCase(deleteTestimonial.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteTestimonial.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = 'Testimonial deleted successfully';
        
        // Remove from userTestimonials array
        state.userTestimonials = state.userTestimonials.filter(
          (item) => item._id !== action.payload
        );
        
        // Remove from all testimonials array
        state.testimonials = state.testimonials.filter(
          (item) => item._id !== action.payload
        );
      })
      .addCase(deleteTestimonial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { clearTestimonialState, clearError } = testimonialSlice.actions;
export default testimonialSlice.reducer;